import { useState, useCallback, useRef } from "react";
import { Icon } from "@iconify/react";
import DatePickerPopover from "./DatePickerPopover";
import "./CustomDatePicker.css";

/**
 * CustomDatePicker — optional, MUI-inspired, fully functional.
 *
 * Props:
 *  - name          {string}   field name (default: "fecha_nacimiento")
 *  - label         {string}   floating label
 *  - value         {string}   controlled value (YYYY-MM-DD)
 *  - onChange      {fn}       (name, value) => void
 *  - error         {object}   { message } from react-hook-form
 *  - required      {boolean}
 */
const CustomDatePicker = ({
  name = "fecha_nacimiento",
  label = "Fecha de nacimiento (opcional)",
  value,
  onChange,
  error,
  required = false,
}) => {
  const today = new Date();
  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState("days");
  const [displayDate, setDisplayDate] = useState({
    month: today.getMonth(),
    year: today.getFullYear(),
  });

  const wrapperRef = useRef(null);

  const formatDisplay = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  };

  const handlePrev = useCallback(() => {
    setDisplayDate((prev) => {
      if (viewMode === "days") {
        const d = new Date(prev.year, prev.month - 1, 1);
        return { month: d.getMonth(), year: d.getFullYear() };
      }
      if (viewMode === "months") return { ...prev, year: prev.year - 1 };
      return { ...prev, year: prev.year - 10 };
    });
  }, [viewMode]);

  const handleNext = useCallback(() => {
    setDisplayDate((prev) => {
      if (viewMode === "days") {
        const d = new Date(prev.year, prev.month + 1, 1);
        return { month: d.getMonth(), year: d.getFullYear() };
      }
      if (viewMode === "months") return { ...prev, year: prev.year + 1 };
      return { ...prev, year: prev.year + 10 };
    });
  }, [viewMode]);

  const handleToggleView = useCallback(() => {
    setViewMode((v) =>
      v === "days" ? "years" : v === "years" ? "months" : "days",
    );
  }, []);

  const handleSelectDay = useCallback(
    (dateStr) => {
      onChange?.(name, dateStr);
      setOpen(false);
      setViewMode("days");
    },
    [name, onChange],
  );

  const handleSelectMonth = useCallback((monthIndex) => {
    setDisplayDate((prev) => ({ ...prev, month: monthIndex }));
    setViewMode("days");
  }, []);

  const handleSelectYear = useCallback((year) => {
    setDisplayDate((prev) => ({ ...prev, year }));
    setViewMode("months");
  }, []);

  const handleClear = useCallback(() => {
    onChange?.(name, "");
  }, [name, onChange]);

  const handleToday = useCallback(() => {
    const iso = today.toISOString().split("T")[0];
    onChange?.(name, iso);
    setDisplayDate({ month: today.getMonth(), year: today.getFullYear() });
    setOpen(false);
    setViewMode("days");
  }, [name, onChange]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setViewMode("days");
  }, []);

  const isFilled = Boolean(value);

  return (
    <div className="dp-trigger-wrapper" ref={wrapperRef}>
      <input
        type="text"
        readOnly
        value={formatDisplay(value)}
        className={[
          "dp-input",
          open ? "dp-input--open" : "",
          isFilled ? "dp-input--filled" : "",
          error ? "dp-input--error" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        placeholder=" "
        onClick={() => setOpen((v) => !v)}
        aria-label={label}
        aria-required={required}
        aria-invalid={!!error}
        autoComplete="off"
      />

      <label className="dp-input-label">{label}</label>

      <div className="dp-icon-btn">
        <Icon
          icon={open ? "mdi:calendar-check" : "mdi:calendar-month-outline"}
          width="20"
        />
      </div>

      {error && (
        <p className="dp-error-msg">
          <Icon icon="mdi:alert-circle-outline" width="14" />
          {error.message}
        </p>
      )}

      <DatePickerPopover
        open={open}
        viewMode={viewMode}
        displayDate={displayDate}
        selectedDate={value}
        onPrev={handlePrev}
        onNext={handleNext}
        onSelectDay={handleSelectDay}
        onSelectMonth={handleSelectMonth}
        onSelectYear={handleSelectYear}
        onToggleView={handleToggleView}
        onClear={handleClear}
        onToday={handleToday}
        onClose={handleClose}
      />
    </div>
  );
};

export default CustomDatePicker;
